import classNames from '@utils/classnames';
import { CalendarDotsIcon, CheckIcon, ClockIcon, ListChecksIcon, XIcon } from '@phosphor-icons/react';
import { OrderLineStopType, OrderDocumentType } from 'src/types/enums';
import { formatDate, formatInputTime } from '../../../utils/date';
import { Button } from '../../../components/button/Button';
import { usePendingCustomerOrders } from '../models';
import type { PendingCustomerOrder } from '../endpoints/PendingCustomerOrdersEndpoint.types';
import { DelayedSpinner } from '../../../components/Spinner';

interface IOrderSelectProps {
  customerId: number;
  searchValue: string;
  selectedOrderIds: string[];
  onSelectOrder: (order: PendingCustomerOrder) => void;
  onSelectAllOrders?: (orders: PendingCustomerOrder[]) => void;
}

export const OrderSelect: React.FC<IOrderSelectProps> = (props) => {
  const { customerId, searchValue, selectedOrderIds, onSelectOrder, onSelectAllOrders } = props;
  const { data, isLoading } = usePendingCustomerOrders(customerId, searchValue || undefined);

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  const orders = data ?? [];
  const selectedOrders = new Set(selectedOrderIds);
  const allSelected = orders.length > 0 && orders.every((o) => selectedOrders.has(o.id));

  return (
    <div>
      {!!onSelectAllOrders && orders.length > 0 && (
        <div className="mb-4">
          <Button
            onTrigger={() => {
              onSelectAllOrders(allSelected ? [] : orders);
            }}
            iconLeft={<ListChecksIcon className="button-icon" />}
          >
            {allSelected ? 'Deselecteer alles' : 'Selecteer alles'}
          </Button>
        </div>
      )}
      <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
        {orders.map((o) => {
          const isSelected = selectedOrders.has(o.id);
          return (
            <div
              key={o.id}
              className={classNames('card p-4', {
                'selected-card': isSelected,
              })}
              data-clickable="true"
              onClick={() => {
                onSelectOrder(o);
              }}
            >
              <div className="card-heading-text flex justify-between">
                <div>{o.orderNumber}</div>
                <div>{o.customerRef}</div>
              </div>

              {o.lines.map((line) => {
                return (
                  <div>
                    {line.stops
                      .sort((a, b) => a.sequenceIndex - b.sequenceIndex)
                      .map((stop) => {
                        return (
                          <div className="grid grid-cols-2 my-1">
                            <div className="font-medium">
                              {stop.type === OrderLineStopType.Load ? 'Laden' : 'Lossen'}
                            </div>
                            <div>
                              {stop.location.country}-{stop.location.name}
                            </div>

                            <div className="flex gap-2 items-center">
                              <CalendarDotsIcon className="w-4 h-4" />
                              {formatDate(stop.date)}
                            </div>
                            <div className="flex gap-2 items-center">
                              <ClockIcon className="w-4 h-4" />
                              {`${formatInputTime(stop.timeStart)}-${formatInputTime(stop.timeEnd)}`}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                );
              })}

              <div className="flex font-medium items-center">
                CMR{' '}
                {o.documents.find((d) => d.type === OrderDocumentType.Cmr) ? (
                  <CheckIcon className="w-6 h-6 text-positive-text" />
                ) : (
                  <XIcon className="w-6 h-6 text-negative-text" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
